import { AlertTriangle, Sparkles, Target } from '../../../../icons';
import { cn } from '../../../../lib/utils';
import {
  riskTone,
  VARIANT_INSUFFICIENT_RANGE_SENTINEL,
  type VariantMeta,
} from '../types';

export function VariantHeaderCard({ meta }: { meta: VariantMeta }) {
  const insufficient = meta.creative_direction === VARIANT_INSUFFICIENT_RANGE_SENTINEL;

  if (insufficient) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-[11px] font-bold tracking-wide text-amber-900 uppercase">
              Limited creative range
            </p>
            <p className="mt-0.5 text-caption-regular leading-relaxed text-amber-900/85">
              The model could not produce a meaningfully distinct rewrite for this lane —
              it returned a copy of the safe variant. Treat as the safe variant.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const tone = riskTone(meta.risk_profile);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-violet-500" />
          <span className="text-body-sm-semibold text-foreground">{meta.label}</span>
        </div>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase',
            tone.bg,
            tone.text,
            tone.border,
          )}
        >
          {meta.risk_profile} risk
        </span>
      </div>

      <div className="p-3">
        <p className="text-caption-regular leading-relaxed text-muted-foreground italic">
          <span className="mr-1 font-semibold tracking-wide text-muted-foreground uppercase">
            Direction:
          </span>
          {meta.creative_direction}
        </p>

        {meta.key_changes.length > 0 ? (
          <div className="mt-3">
            <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">
              Key changes
            </p>
            <ul className="mt-1 space-y-1">
              {meta.key_changes.map((c, i) => (
                <li
                  key={i}
                  className="flex items-start gap-1.5 text-caption-medium/relaxed text-foreground"
                >
                  <span className="mt-1.5 size-1 shrink-0 rounded-full bg-violet-400" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {meta.when_to_use ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-border bg-muted px-2.5 py-2">
            <Target size={12} className="mt-0.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">
                Best for
              </p>
              <p className="mt-0.5 text-caption-medium/relaxed text-foreground">
                {meta.when_to_use}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
